export const STAY_ON_LEGACY_PARAM = 'stayOnLegacy'
export const WORKBENCH_HOME_PATH = '/home/modules'

export const WORKBENCH_HOST_BY_DESIGNER_HOST: Record<string, string> = {
    'designer-beta.assessments.recruiting.people-tech.a2z.com':
        'workbench-beta.assessments.recruiting.people-tech.a2z.com',
    'designer-gamma.assessments.recruiting.people-tech.a2z.com':
        'workbench-gamma.assessments.recruiting.people-tech.a2z.com',
    'assessment-designer.assessments.recruiting.people-tech.a2z.com':
        'assessment-workbench.assessments.recruiting.people-tech.a2z.com',
}

export interface RedirectLocation {
    hostname: string
    pathname: string
    search: string
    hash: string
}

const SEGMENT = '[^/?#]+'

const ROUTE_RULES: { pattern: RegExp; redirectTo?: string }[] = [
    { pattern: /^\/$/, redirectTo: WORKBENCH_HOME_PATH },
    { pattern: /^\/import-content$/ },
    { pattern: /^\/apt$/ },
    { pattern: /^\/tree-editor$/ },
    { pattern: new RegExp(`^/buckets-and-cups-visualizer/${SEGMENT}$`) },
    { pattern: /^\/search$/ },
    { pattern: /^\/scoring-test-ui$/ },
    { pattern: /^\/scoring-test-v2$/ },
    { pattern: new RegExp(`^/scoring-test-v2/${SEGMENT}/${SEGMENT}$`) },
    { pattern: new RegExp(`^/module-builder(/${SEGMENT})?$`) },
    { pattern: new RegExp(`^/module/viewer/${SEGMENT}$`) },
    { pattern: new RegExp(`^/module/viewer/${SEGMENT}/review$`) },
    { pattern: new RegExp(`^/module/viewer/${SEGMENT}/review/${SEGMENT}$`) },
    { pattern: new RegExp(`^/module/viewer/${SEGMENT}/review/${SEGMENT}/revisions/${SEGMENT}$`) },
    { pattern: new RegExp(`^/item-pool-generator(/${SEGMENT})?$`) },
    { pattern: new RegExp(`^/history/${SEGMENT}$`) },
    { pattern: /^\/research-workflow$/ },
    { pattern: /^\/workflow-automation-tests$/ },
    { pattern: /^\/asset-library$/ },
    { pattern: /^\/asset-library\/create-asset$/ },
    { pattern: new RegExp(`^/asset-library/assets/${SEGMENT}$`) },
    { pattern: /^\/item-bank-submission$/ },
    { pattern: /^\/item-bank-manager$/ },
    { pattern: /^\/module-groups$/ },
    { pattern: new RegExp(`^/module-groups/${SEGMENT}$`) },
    { pattern: new RegExp(`^/module-groups/${SEGMENT}/review$`) },
    {
        pattern: new RegExp(`^/module-groups/${SEGMENT}/review/${SEGMENT}/revisions/${SEGMENT}$`),
    },
]

const COGNITO_RESPONSE_PARAMS = ['code', 'state', 'error', 'error_description']

const isCognitoResponseHash = (hash: string): boolean =>
    ['access_token=', 'id_token=', 'error='].some((marker) => hash.includes(marker))

const readStoredStayOnLegacy = (): boolean => {
    try {
        return window.sessionStorage.getItem(STAY_ON_LEGACY_PARAM) === 'true'
    } catch {
        return false
    }
}

const storeStayOnLegacy = (stay: boolean): void => {
    try {
        if (stay) {
            window.sessionStorage.setItem(STAY_ON_LEGACY_PARAM, 'true')
        } else {
            window.sessionStorage.removeItem(STAY_ON_LEGACY_PARAM)
        }
    } catch {
        // Nothing to do: without storage the override lasts for this page load only.
    }
}

/**
 * Whether this page load should stay on Designer instead of redirecting.
 *
 * `?stayOnLegacy=true` engages the override and persists it in `sessionStorage` so reloads and
 * follow-up page loads in the same tab stay put; `?stayOnLegacy=false` clears it. It is
 * deliberately `sessionStorage` rather than `localStorage` so the override dies with the tab.
 */
export const isStayOnLegacyRequested = (location: RedirectLocation): boolean => {
    const requested = new URLSearchParams(location.search).get(STAY_ON_LEGACY_PARAM)
    if (requested === 'true' || requested === 'false') {
        const stay = requested === 'true'
        storeStayOnLegacy(stay)
        return stay
    }
    return readStoredStayOnLegacy()
}

/** Maps a Designer path to its Workbench path, sending anything unrecognized to the home page. */
const mapPath = (pathname: string): string => {
    // Normalize so `/module-groups/` and `/module-groups` map the same way.
    const path = `/${pathname.replace(/^\/+|\/+$/g, '')}`
    const rule = ROUTE_RULES.find(({ pattern }) => pattern.test(path))
    if (!rule) {
        return WORKBENCH_HOME_PATH
    }
    return rule.redirectTo ?? path
}

/** The Workbench URL equivalent to `location`, or `null` if this host does not redirect. */
export const resolveWorkbenchRedirectUrl = (location: RedirectLocation): string | null => {
    const target = WORKBENCH_HOST_BY_DESIGNER_HOST[location.hostname]
    if (!target) {
        return null
    }

    const params = new URLSearchParams(location.search)
    params.delete(STAY_ON_LEGACY_PARAM)
    COGNITO_RESPONSE_PARAMS.forEach((param) => params.delete(param))
    const search = params.toString()
    const hash = isCognitoResponseHash(location.hash) ? '' : location.hash

    return `https://${target}${mapPath(location.pathname)}${search ? `?${search}` : ''}${hash}`
}

/** Redirects to Workbench when the current host and URL call for it, returning whether it did. */
export const applyWorkbenchRedirect = (): boolean => {
    if (!WORKBENCH_HOST_BY_DESIGNER_HOST[window.location.hostname]) {
        return false
    }
    if (isStayOnLegacyRequested(window.location)) {
        console.info(
            `[workbench-redirect] Staying on Assessment Designer because ?${STAY_ON_LEGACY_PARAM}=true is set for this tab. ` +
                `Append ?${STAY_ON_LEGACY_PARAM}=false to resume redirecting.`
        )
        return false
    }
    const url = resolveWorkbenchRedirectUrl(window.location)
    if (!url) {
        return false
    }
    console.info(
        `[workbench-redirect] Redirecting to Assessment Workbench: ${url}. ` +
            `Append ?${STAY_ON_LEGACY_PARAM}=true to stay on Assessment Designer.`
    )
    window.location.replace(url)
    return true
}
